// src/components/adjustments/IssuedAdjustmentEditor.jsx
//
// Adjustments -> "Cash Issued" / "Bank Issued": edits an existing Cash/Bank
// Issued transaction (money OUT to an Other Ledger), including entering a
// negative amount to correct a wrongly recorded one. Only reachable by the
// restricted adjustment accounts (NEW_USER role / the any Super Admin account
// account) - CategoryAdjustmentPanel disables the Edit button for everyone
// else, and the server enforces the same on every route this calls.
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../api';

const IssuedAdjustmentEditor = ({ embeddedId, category, onCancel, onSaved }) => {
  const isBank = category === 'Bank Issued';
  const endpoint = isBank ? `/finance/bank/issued/${embeddedId}` : `/finance/cash/issued/${embeddedId}`;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get(endpoint).then(({ data }) => {
      if (!active) return;
      setForm({
        date: String(data.date || '').slice(0, 10),
        amount: String(data.amount ?? ''),
        reference_number: data.reference_number || '',
        remark: data.remark || '',
        other_ledger_name: data.other_ledger_name || '',
        bank_name: data.bank_name || '',
        account_number: data.account_number || '',
        company_name: data.company_name || ''
      });
    }).catch((loadError) => {
      if (active) setError(loadError.response?.data?.error || 'Unable to load this record.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [endpoint]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    const parsedAmount = Number(form.amount);
    if (!form.date) return setError('Date is required.');
    if (!Number.isFinite(parsedAmount) || parsedAmount === 0) return setError('Amount must not be zero.');

    setSaving(true);
    setError('');
    try {
      await api.put(endpoint, {
        date: form.date,
        amount: parsedAmount,
        reference_number: form.reference_number || null,
        remark: form.remark || null
      });
      onSaved?.();
    } catch (saveError) {
      setError(saveError.response?.data?.error || `Unable to save ${category.toLowerCase()} adjustment.`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p><FontAwesomeIcon icon={faSpinner} spin /> Loading {category.toLowerCase()} details...</p>;
  if (!form) {
    return <div className="form-section">
      <div className="alert-message error">{error}</div>
      <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={onCancel}>Back</button></div>
    </div>;
  }

  return (
    <form className="adjustments-form" onSubmit={save}>
      <h3>Edit {category} ({form.other_ledger_name})</h3>
      <div className="form-grid">
        <label className="form-group">{isBank ? 'Bank Account' : 'Company'}
          <input className="form-input" value={isBank ? `${form.bank_name}${form.account_number ? ` — ${form.account_number}` : ''}` : form.company_name} disabled />
        </label>
        <label className="form-group">Date <span className="required">*</span>
          <input className="form-input" type="date" value={form.date} required onChange={(event) => change('date', event.target.value)} />
        </label>
        <label className="form-group">Amount (Rs.) <span className="required">*</span>
          <input className="form-input" type="number" step="0.01" value={form.amount} required
            onChange={(event) => change('amount', event.target.value)} />
          <span className="help-text">A negative amount corrects a wrongly recorded transaction.</span>
        </label>
        <label className="form-group">Reference No.
          <input className="form-input" maxLength={100} value={form.reference_number}
            onChange={(event) => change('reference_number', event.target.value)} />
        </label>
        <label className="form-group">Description / Remark
          <textarea className="form-textarea" maxLength={255} rows={3} value={form.remark}
            onChange={(event) => change('remark', event.target.value)} />
        </label>
      </div>
      {error && <div className="alert-message error">{error}</div>}
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save Adjustment'}
        </button>
      </div>
    </form>
  );
};

export default IssuedAdjustmentEditor;
